/**
 * @file Waitlist-aware marketing copy for the www chrome + landing CTA.
 * @module @labcraft/www/ui/waitlist-copy
 *
 * One place owns the two copy states so the capture surfaces never
 * drift. Which state renders is decided by `waitlistEnabled` from
 * `/api/config` (the live mirror of the account `waitlist_learners`
 * setting), read by the `signin-cta` island; call sites pick the
 * variant with `WAITLIST_COPY[on ? 'on' : 'off']`.
 *
 * `off` is the public-beta default and the fallback rendered while
 * `/api/config` is in flight; `on` remains available for capacity-limited
 * waitlist mode. Editing the public wording happens here (canon mirror at
 * `design/www/...` per `.agents/rules/copy.md`).
 */

/**
 * The copy strings that switch with waitlist mode.
 *
 * @property signInLabel - Label for the nav sign-in button + its
 *   popover heading (`TopNav` `signInLabel` prop).
 * @property ctaTitle - Heading of the landing's closing `SignInCTA`.
 * @property ctaLede - One-line subhead under that heading.
 * @property navCta - Label for the nav/hero forward-action button
 *   (desktop nav island, mobile menu, landing hero) that anchors to
 *   the closing capture section (`#start`).
 */
export type WaitlistCopyStrings = {
  signInLabel: string
  ctaTitle: string
  ctaLede: string
  navCta: string
}

/**
 * Copy keyed by waitlist mode. `on` ⇒ signups join the waitlist; `off` ⇒
 * immediate access during the public beta.
 */
export const WAITLIST_COPY: {
  on: WaitlistCopyStrings
  off: WaitlistCopyStrings
} = {
  on: {
    signInLabel: 'Request access',
    ctaTitle: 'Claim your spot.',
    ctaLede:
      "LabCraft is in early access. Sign in to claim your spot; we'll reach out as we open the next wave.",
    navCta: 'Get early access →',
  },
  off: {
    signInLabel: 'Sign in',
    ctaTitle: 'Start your first lab.',
    ctaLede:
      'LabCraft is open as a public beta. GitHub, Google, or a magic link to your inbox — pick your way in.',
    navCta: 'Start your first lab →',
  },
}
